import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { ArrowLeft, Mail, Building2 } from 'lucide-react';
import { authService } from '../services/authService';

interface ForgotPasswordFormData {
  email: string;
}

interface ForgotPasswordScreenProps {
  onBackToLogin?: () => void;
}

export function ForgotPasswordScreen({ onBackToLogin }: ForgotPasswordScreenProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [email, setEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const normalizedEmail = data.email.toLowerCase().trim();
      await authService.forgotPassword(normalizedEmail);
      setEmail(normalizedEmail);
      setIsSuccess(true);
      setCountdown(90);
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send reset email';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setIsLoading(true);
    setError(null);

    try {
      await authService.forgotPassword(email);
      setCountdown(90);
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send reset email';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <div className="w-full max-w-md">
          {/* Company Branding */}
          <div className="text-center mb-8 animate-fade-in">
             <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4 shadow-xl ring-2 ring-blue-400/20 animate-pulse" style={{ backgroundColor: '#001F54' }}>
              <img 
                src="/login_logo.png" 
                alt="Aura Logo" 
                className="w-16 h-16 object-contain filter brightness-0 invert"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
               <Building2 className="w-12 h-12 text-white hidden" />
            </div>
          </div>

           <Card>
             <CardContent className="p-8 shadow-lg border border-gray-200">
             <div className="text-center">
               <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-6 mt-4">
                 <Mail className="h-6 w-6 text-[#001F54]" />
               </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Check Your Email
              </h2>
              
              <p className="text-gray-600 mb-6">
                Password reset link sent to your email.
              </p>

              <div className="space-y-4">
                 <Button
                   onClick={() => window.location.href = '/login'}
                   className="w-full h-12 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                   style={{ backgroundColor: '#001F54' }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.backgroundColor = '#002a6b';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.backgroundColor = '#001F54';
                   }}
                 >
                   Back to Login
                 </Button>
                
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">
                    Didn't receive the email?
                  </p>
                  <button
                    onClick={handleResend}
                    disabled={countdown > 0 || isLoading}
                    className={`text-sm font-medium transition-colors ${
                      countdown > 0 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-[#001F54] hover:underline'
                    }`}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Email'}
                  </button>
                </div>
              </div>
              
              <div className="text-center mt-6">
                <p className="text-xs text-gray-500">
                  © 2025 AuraHub. All rights reserved.
                </p>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-gray-500 underline hover:text-gray-700">
                  Privacy Policy
                </a>
              </div>
            </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="w-full max-w-md">
        {/* Company Branding */}
        <div className="text-center mb-8 animate-fade-in">
           <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4 shadow-xl ring-2 ring-blue-400/20 animate-pulse" style={{ backgroundColor: '#001F54' }}>
            <img 
              src="/login_logo.png" 
              alt="Aura Logo" 
              className="w-16 h-16 object-contain filter brightness-0 invert"
              onError={(e) => {
                // Fallback to icon if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
             <Building2 className="w-12 h-12 text-white hidden" />
          </div>
        </div>

        <Card className="border border-gray-200 shadow-xl animate-slide-in bg-white rounded-2xl overflow-hidden">
          <CardHeader className="space-y-1 pb-2">
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <Label htmlFor="email" className="text-black font-medium text-sm">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#001F54]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    className="pl-12 h-12 border-gray-300 focus:border-[#001F54] focus:ring-[#001F54] rounded-lg"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#001F54' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#002a6b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#001F54';
                }}
              >
                {isLoading ? 'Sending...' : 'Reset'}
              </Button>
            </form>
            
            <div className="text-center mt-6">
              <Link to="/login">
                <Button
                  variant="outline"
                  className="inline-flex items-center bg-[#001F54] hover:bg-[#001A4A] text-white border-[#001F54] hover:border-[#001A4A]"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Login
                </Button>
              </Link>
            </div>
            
            <div className="text-center mt-6">
              <p className="text-xs text-gray-500">
                © 2025 AuraHub. All rights reserved.
              </p>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-gray-500 underline hover:text-gray-700">
                Privacy Policy
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
